import { useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import clsx from "clsx";
import { Button, CoverBleed, coverBleedHost, EmptyState, Skeleton, Icon } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useMobileBack } from "../../app/mobileBack.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { useRelease } from "../../app/hooks/useRelease.js";
import { ReleaseArtwork } from "./ReleaseArtwork.js";
import { ReleaseMobileSummary } from "./ReleaseMobileSummary.js";
import { ShareLinkBar } from "./ShareLinkBar.js";
import { ReleaseSectionNavContext, SectionRailContext } from "./sectionRailContext.js";
import {
  DEFAULT_SECTION,
  sectionLabel,
  visibleSections,
  type ReleaseSectionKey,
} from "./sections.js";
import {
  countdownLabel,
  formatDottedDate,
  releaseCountdownToken,
  releaseCreditLine,
  releaseGradient,
  releaseStatusPhrase,
  releaseStatusTone,
  releaseTypeLabel,
} from "./releaseFormat.js";
import styles from "./ReleaseDetailScreen.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

// The release COMMAND CENTER shell (shots 09–16) — chrome around the routed section Outlet: breadcrumb,
// gradient hero + activity panel, the private share-link bar, the numbered section strip, and the Prev/Next
// pager. The visible sections drop 08 Budget for a seat without money:marketing (`visibleSections`), so the
// strip and pager read that gated list. Deep-linkable + refresh-safe: the active section rides the URL and
// every section reads the same composed `useRelease` query (deduped). A deep-link to a gated section (e.g.
// `/budget` without money) degrades to a notice, never a broken pager. The hero repaints on `release-changed`.

export interface ReleaseDetailScreenProps {
  releaseId: string;
  activeSection: ReleaseSectionKey;
  onSection: (key: ReleaseSectionKey) => void;
  onBack: () => void;
  /** The active section body — the routed `<Outlet/>`. */
  children: ReactNode;
}

export function ReleaseDetailScreen({
  releaseId,
  activeSection,
  onSection,
  onBack,
  children,
}: ReleaseDetailScreenProps) {
  const q = useRelease(releaseId);
  // The command-center is a BUILDER; spec §3 keeps the editor desktop-only ("mobile = catalogue +
  // per-release stats, no editor"). Below the shell swap (`lg`, the same threshold `ShellLayout` uses) the
  // hero stays but the editor — share bar, section strip, the routed section `<Outlet/>`, the pager — is
  // replaced by a read-only stats summary, so a phone (or a deep link to any section on a phone) can never
  // open the squeezed builder (ART-153 pass two).
  const isDesktop = !usePhoneLayout();
  // On mobile, back rides the shell header's leading slot (logo-replacement); desktop keeps the breadcrumb's
  // "Releases" link. Registering null on desktop leaves the header untouched.
  useMobileBack(isDesktop ? null : onBack);
  const overview = q.data?.data?.overview;
  // The action-rail column's DOM node — filled by the <aside> ref below, then handed to each section through
  // context so it can portal its buttons in (ART-201). A callback ref into state gives the portal the two-pass
  // render it needs: null on the first pass, the node on the second.
  const [railEl, setRailEl] = useState<HTMLElement | null>(null);
  // Whether 08 Budget is offered at all — see visibleSections. Read from the capability rather than from
  // `data.budget == null`, which would also be null while the read is in flight and would flicker the strip.
  const canSeeMarketingMoney = useCapability("money:marketing");
  // The hero's ground. Resolved through the SAME (mediaId, variant "sm") key `ReleaseArtwork` uses for its
  // `tile` slot below, so the resolver cache serves the thumbnail and the bleed from ONE fetch.
  const cover = useMediaObjectUrl(overview?.coverMediaId ?? null, overview?.coverImageUrl ?? null, { variant: "sm" });
  const coverUrl = cover.error ? null : cover.url;

  // Switching sections KEEPS the reader's scroll position (Ryan, ART-201 — "keep you on that spot, not back
  // to the top every time"). The reference's scroll-to-top-on-change effect was removed for exactly that.

  // The guard is `data == null`, not a bare `isError`: in TanStack v5 a failed BACKGROUND refetch sets
  // status:"error" with `data` still populated, and this record is invalidated live by both the
  // `release-changed` and `task-changed` signals — so an unguarded branch replaces a fully-painted command
  // centre on any transient failure. A cold-load failure still shows the one designed state.
  if (q.isError && q.data == null) {
    return (
      <section className={styles.page}>
        <Breadcrumb title="Release" onBack={onBack} />
        <EmptyState
          title="This release isn't available"
          description={q.error instanceof Error && q.error.message ? q.error.message : "It may have been cancelled."}
          action={<Button onClick={onBack}>Back to releases</Button>}
        />
      </section>
    );
  }

  if (q.isLoading || !overview) {
    return (
      <section className={styles.page}>
        <Breadcrumb title="Release" onBack={onBack} />
        <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading release">
          <Skeleton variant="tile" />
          <Skeleton variant="line" width="60%" />
          <Skeleton variant="line" width="40%" />
        </div>
      </section>
    );
  }

  // The sections visible for THIS seat (no money:marketing hides 08 Budget, whose every figure is money). The
  // strip and pager both read this gated list so they never disagree. activeIndex is -1 when the URL points at
  // a section gated off this seat — which is what makes a deep link to /budget land on the "not part of your
  // access" notice rather than a broken section.
  const sections = visibleSections(canSeeMarketingMoney);
  const activeIndex = sections.findIndex((s) => s.key === activeSection);
  const activeVisible = activeIndex >= 0;
  const idx = activeIndex + 1; // 1-based; 0 when the active section is gated off this type
  const prev = activeIndex > 0 ? sections[activeIndex - 1] : null;
  const next = activeVisible && activeIndex < sections.length - 1 ? sections[activeIndex + 1] : null;

  return (
    <section className={styles.page}>
      <Breadcrumb title={overview.title || "Untitled release"} onBack={onBack} />

      {/* The ground is this release's own artwork, blurred (PO, 2026-09-10) — the same `CoverBleed` the hub
          banner wears, so the two heroes cannot drift. `releaseGradient` stays as the fallback beneath it:
          a release with no cover renders the born-blank identity gradient, which is what `CoverBleed` leaves
          alone by returning nothing. */}
      <header
        className={clsx(styles.hero, coverUrl && coverBleedHost)}
        style={{ background: releaseGradient(releaseId) }}
      >
        <CoverBleed url={coverUrl} />
        <ReleaseArtwork
          seed={releaseId}
          mediaId={overview.coverMediaId}
          imageUrl={overview.coverImageUrl}
          size="tile"
          className={styles.heroArt}
          lazy={false}
        />
        <div className={styles.heroMain}>
          {/* The status/type EYEBROW, above the h1.
              PO 2026-09-09 (ART-99) — a NARROW reversal of ruling 10 ("an h1 never carries an eyebrow",
              2026-08-17), for this hero only. The ruling's reasoning was that the category label above a page
              title "was saying what the nav already said"; here it does not — this line is the release's live
              lifecycle, and the reference has always carried it above the title. `headings.guard.test.ts`
              names this file as its one exemption; the other twelve sites the 2026-08-17 sweep touched keep
              the ruling. Do not generalise the carve-out by copying this block. */}
          <p className={styles.heroEyebrow} data-tone={releaseStatusTone(overview.status)}>
            <span className={styles.statusDot} aria-hidden="true" /> {releaseStatusPhrase(overview.status)} ·{" "}
            {releaseTypeLabel(overview.releaseType)}
          </p>
          <h1 className={styles.heroTitle}>{overview.title || "Untitled release"}</h1>
          {/* The reference's three-part credit line, hairline-separated: countdown · date · who put it out.
              The countdown is TINTED and the only coloured text on the hero — it is the one figure here that
              changes on its own, so it is what a band re-reads. */}
          <p className={styles.heroMeta}>
            <span className={styles.heroCd}>{releaseCountdownToken(overview.releaseDate)}</span>
            {formatDottedDate(overview.releaseDate) && (
              <>
                <i className={styles.heroSep} aria-hidden="true" />
                <span>{formatDottedDate(overview.releaseDate)}</span>
              </>
            )}
            <i className={styles.heroSep} aria-hidden="true" />
            <span>{releaseCreditLine(overview.bandName, overview.labelName)}</span>
          </p>
        </div>
        <aside className={styles.activity} aria-label="Activity">
          <div className={styles.activityHead}>
            <span>Activity</span>
            <span className={styles.livePill}>
              <span className={styles.liveDot} aria-hidden="true" /> Live
            </span>
          </div>
          <p className={styles.activityLine}>
            {overview.status === "released" ? "Rollout in progress" : "Rollout underway"},{" "}
            <strong>{countdownLabel(overview.releaseDate) || "no date set"}</strong>
          </p>
        </aside>
      </header>

      {isDesktop ? (
        <>
          <ShareLinkBar releaseId={releaseId} />

          {/* Section tabs (Ryan, ART-201 — reverted from pills to the SQUARE/underline style, like Settings):
              a bottom hairline under the strip, the active section carrying a crimson underline. The active
              section's ACTIONS share this row, right-aligned (ART-278): each section portals its buttons into
              the host beside the tabs, where a column beside the body used to hold them and waste its width. */}
          <div className={styles.stripRow}>
            <nav className={styles.strip} aria-label="Release sections">
              {sections.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={styles.stripTab}
                  data-active={s.key === activeSection || undefined}
                  aria-current={s.key === activeSection ? "page" : undefined}
                  onClick={() => onSection(s.key)}
                >
                  <span className={styles.stripNum}>{s.num}</span>
                  <span className={styles.stripLabel}>{s.label}</span>
                </button>
              ))}
            </nav>
            <div
              className={styles.actions}
              ref={setRailEl}
              role="group"
              aria-label={`${sectionLabel(activeSection)} actions`}
            />
          </div>

          <ReleaseSectionNavContext.Provider value={onSection}>
          <SectionRailContext.Provider value={railEl}>
            <div className={styles.body}>
              {activeVisible ? (
                children
              ) : (
                // A deep-link to a section gated off this seat (e.g. `/budget` without money:marketing) —
                // degrade to a notice rather than render an orphaned section body with a broken pager.
                <EmptyState
                  title="Not part of your access"
                  description={`The ${sectionLabel(activeSection)} section isn't part of your access to this release.`}
                  action={<Button onClick={() => onSection(DEFAULT_SECTION)}>Back to Overview</Button>}
                />
              )}
            </div>
          </SectionRailContext.Provider>
          </ReleaseSectionNavContext.Provider>

          {activeVisible && (
            <footer className={styles.pager}>
              <span className={styles.pagerMeta}>
                Section {idx} of {sections.length}
              </span>
              <div className={styles.pagerButtons}>
                <Button variant="ghost" size="sm" disabled={!prev} onClick={() => prev && onSection(prev.key)}>
                  <Icon icon={ArrowLeft} size={16} /> Prev
                </Button>
                <Button variant="ghost" size="sm" disabled={!next} onClick={() => next && onSection(next.key)}>
                  Next <Icon icon={ArrowRight} size={16} />
                </Button>
              </div>
            </footer>
          )}
        </>
      ) : (
        // Spec §3: no editor on mobile. The hero above stays; the builder is replaced by a read-only stats view.
        <ReleaseMobileSummary releaseId={releaseId} />
      )}
    </section>
  );
}

function Breadcrumb({ title, onBack }: { title: string; onBack: () => void }) {
  // On mobile the shell header carries the back (logo-replacement) + the "RELEASES" section title, so the
  // "Releases /" crumb link is redundant there — render just the current release name.
  const isDesktop = !usePhoneLayout();
  return (
    <nav className={styles.crumb} aria-label="Breadcrumb">
      {isDesktop && (
        <>
          <button type="button" className={styles.crumbLink} onClick={onBack}>
            Releases
          </button>
          <span className={styles.crumbSep} aria-hidden="true">
            /
          </span>
        </>
      )}
      <span className={styles.crumbCurrent}>{title}</span>
    </nav>
  );
}
